import axios from "axios";

/**
 * Public market rates for the converter and the market Dashboard.
 *
 * These come straight from CoinGecko's public API. They are *not* GoldBot
 * telemetry: no account, no Clerk session, and no OMJ VISION backend is
 * involved, which is why this client is a bare axios instance rather than one
 * from `apiClient.js`.
 *
 * The converter previously multiplied by a table of round numbers written into
 * the source (BTC at exactly $100,000, gold at exactly $2,000). Those were
 * invented, and a user had no way to tell. Everything below reads a real quote
 * or reports that it could not.
 */

const COINGECKO_SIMPLE_PRICE_URL =
  "https://api.coingecko.com/api/v3/simple/price";

/** The assets the converter offers, and their CoinGecko ids. */
export const ASSET = Object.freeze({
  USD: "usd",
  BTC: "btc",
  ETH: "eth",
  XAU: "xau",
});

/** `tether-gold` is a token redeemable for one troy ounce of gold. */
const COINGECKO_IDS = Object.freeze({
  [ASSET.BTC]: "bitcoin",
  [ASSET.ETH]: "ethereum",
  [ASSET.XAU]: "tether-gold",
});

export const ASSET_LABELS = Object.freeze({
  [ASSET.USD]: "USD",
  [ASSET.BTC]: "BTC",
  [ASSET.ETH]: "ETH",
  [ASSET.XAU]: "Gold (XAU)",
});

export const ASSET_OPTIONS = Object.freeze([
  ASSET.USD,
  ASSET.BTC,
  ASSET.ETH,
  ASSET.XAU,
]);

export class MarketRatesError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = "MarketRatesError";
    this.cause = cause;
  }
}

const client = axios.create({ timeout: 8000 });

/**
 * Current USD price of one unit of each supported asset.
 *
 * USD is omitted from the response because it is 1 by definition; `convert`
 * handles it directly. An asset the upstream did not quote is simply absent
 * from the returned object rather than defaulted, so a missing quote surfaces
 * as "unavailable" instead of as a wrong number.
 *
 * @param {{signal?: AbortSignal}} [options]
 * @returns {Promise<{fetchedAt: Date, usdPrices: Record<string, number>}>}
 */
export async function fetchUsdRates({ signal } = {}) {
  let response;

  try {
    response = await client.get(COINGECKO_SIMPLE_PRICE_URL, {
      params: {
        ids: Object.values(COINGECKO_IDS).join(","),
        vs_currencies: "usd",
      },
      signal,
    });
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    throw new MarketRatesError("Could not reach the market data service.", error);
  }

  const payload = response.data;

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new MarketRatesError("The market data service returned an unreadable response.");
  }

  const usdPrices = {};

  for (const [asset, coingeckoId] of Object.entries(COINGECKO_IDS)) {
    const quote = payload[coingeckoId];
    const price = quote && quote.usd;

    if (typeof price === "number" && Number.isFinite(price) && price > 0) {
      usdPrices[asset] = price;
    }
  }

  if (Object.keys(usdPrices).length === 0) {
    throw new MarketRatesError("The market data service returned no usable prices.");
  }

  return { fetchedAt: new Date(), usdPrices };
}

/**
 * Converts between two assets by way of their USD prices.
 *
 * Returns `null` when either leg has no quote — the caller renders
 * "unavailable" rather than a number that would look authoritative.
 *
 * @param {number} amount
 * @param {string} from
 * @param {string} to
 * @param {Record<string, number>} usdPrices USD price of one unit of each asset
 * @returns {number|null}
 */
export function convert(amount, from, to, usdPrices) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    return null;
  }

  const fromUsd = usdPriceOf(from, usdPrices);
  const toUsd = usdPriceOf(to, usdPrices);

  if (fromUsd === null || toUsd === null) {
    return null;
  }

  return (amount * fromUsd) / toUsd;
}

/** USD price of one unit of `asset`; USD is 1 by definition. */
function usdPriceOf(asset, usdPrices) {
  if (asset === ASSET.USD) {
    return 1;
  }

  const price = usdPrices && usdPrices[asset];
  return typeof price === "number" && Number.isFinite(price) && price > 0 ? price : null;
}

/**
 * Formats a converted amount with enough precision to stay meaningful across
 * six orders of magnitude — 0.00009 BTC must not round to 0.00.
 *
 * @param {number} value
 * @returns {string}
 */
export function formatConverted(value) {
  const magnitude = Math.abs(value);
  const decimals = magnitude === 0 ? 2 : magnitude >= 1000 ? 2 : magnitude >= 1 ? 4 : 8;

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  });
}
